import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, CardBody, CardHeader, UncontrolledTooltip } from 'reactstrap';
import { getPaginasDeReferencia } from '../../../api/reportApi';
import TableContainer from '../../../components/TableContainerReactTable';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface ReferrerCardProps {
  campaignId: string;
}

export interface ReferrerCardRef {
  refresh: () => void;
}

const ReferrerCard = forwardRef<ReferrerCardRef, ReferrerCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const referrerData = await getPaginasDeReferencia(parseInt(campaignId));
        setData(referrerData);
      } catch (err) {
        console.error('Error fetching referrer data:', err);
        setError('Erro ao carregar dados de páginas de referência');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId, fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <div className="d-flex align-items-center">
              <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                <i className="ri-external-link-line text-info fs-4"></i>
              </div>
              <div>
                <h4 className={styles.cardTitle + ' mb-0'}>Origem do Tráfego</h4>
                <small className="text-muted">De onde vêm os usuários</small>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="text-center text-danger">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          </CardBody>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={styles.reportCard}>
          <CardHeader className={styles.cardHeader}>
            <div className="d-flex align-items-center">
              <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                <i className="ri-external-link-line text-info fs-4"></i>
              </div>
              <div>
                <h4 className={styles.cardTitle + ' mb-0'}>Origem do Tráfego</h4>
                <small className="text-muted">De onde vêm os usuários</small>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="text-center text-muted">
              <i className="ri-information-line me-2"></i>
              Nenhuma origem de tráfego identificada
            </div>
          </CardBody>
        </Card>
      );
    }

    // Column definitions for the table
    const columns = [
      {
        header: 'Origem do Tráfego',
        accessorKey: 'referrerHostname',
        cell: (row: any) => {
          const hostname = row.getValue() === 'null' ? '(Direto)' : row.getValue();
          const isDirect = hostname === '(Direto)';
          const isSocial = ['facebook.com', 'instagram.com', 'twitter.com', 'linkedin.com', 'tiktok.com'].some(social => 
            hostname.toLowerCase().includes(social)
          );
          const isSearch = ['google.com', 'bing.com', 'yahoo.com', 'duckduckgo.com'].some(search => 
            hostname.toLowerCase().includes(search)
          );
          
          let icon = 'ri-global-line';
          let color = 'text-muted';
          
          if (isDirect) {
            icon = 'ri-home-line';
            color = 'text-primary';
          } else if (isSocial) {
            icon = 'ri-share-line';
            color = 'text-info';
          } else if (isSearch) {
            icon = 'ri-search-line';
            color = 'text-success';
          }
          
          return (
            <div className="d-flex align-items-center">
              <i className={`${icon} ${color} me-2`}></i>
              <span className="fw-medium">{hostname}</span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        id: 'impression',
        header: 'Impressões',
        accessorKey: '_value',
        cell: (row: any) => {
          const value = row.getValue() || 0;
          const total = data.reduce((sum, item) => sum + (item._value || 0), 0);
          const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
          
          return (
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-semibold">{numeral(value).format(numeralInteger)}</span>
              <span className="badge bg-primary bg-opacity-10 text-primary px-2 py-1">
                {percentage}%
              </span>
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <Card className={styles.reportCard}>
        <CardHeader className={styles.cardHeader}>
          <div className="d-flex align-items-center">
            <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
              <i className="ri-external-link-line text-info fs-4"></i>
            </div>
            <div>
              <h4 className={styles.cardTitle + ' mb-0'}>Origem do Tráfego</h4>
              <small className="text-muted">De onde vêm os usuários</small>
            </div>
            <div className="ms-auto">
              <i 
                id="referrer-tooltip" 
                className="ri-question-line text-muted fs-5" 
                style={{ cursor: 'help' }}
              ></i>
              <UncontrolledTooltip placement="top" target="referrer-tooltip">
                Mostra de onde os usuários vieram antes de acessar seu anúncio: redes sociais, outros sites, busca direta, etc.
              </UncontrolledTooltip>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {/* Summary Cards - Compact Layout */}
          <div className="row g-2 mb-4">
            <div className="col-6">
              <div className="d-flex align-items-center p-2 bg-light rounded-2 border">
                <div className="bg-primary bg-opacity-10 rounded-circle p-1 me-2">
                  <i className="ri-global-line text-primary"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-bold text-primary fs-5">{data.length}</div>
                  <small className="text-muted">Fontes</small>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex align-items-center p-2 bg-light rounded-2 border">
                <div className="bg-success bg-opacity-10 rounded-circle p-1 me-2">
                  <i className="ri-share-line text-success"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-bold text-success fs-5">
                    {data.filter(item => {
                      const hostname = item.referrerHostname?.toLowerCase() || '';
                      return ['facebook.com', 'instagram.com', 'twitter.com', 'linkedin.com', 'tiktok.com'].some(social => 
                        hostname.includes(social)
                      );
                    }).length}
                  </div>
                  <small className="text-muted">Sociais</small>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex align-items-center p-2 bg-light rounded-2 border">
                <div className="bg-info bg-opacity-10 rounded-circle p-1 me-2">
                  <i className="ri-search-line text-info"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-bold text-info fs-5">
                    {data.filter(item => {
                      const hostname = item.referrerHostname?.toLowerCase() || '';
                      return ['google.com', 'bing.com', 'yahoo.com', 'duckduckgo.com'].some(search => 
                        hostname.includes(search)
                      );
                    }).length}
                  </div>
                  <small className="text-muted">Busca</small>
                </div>
              </div>
            </div>
            <div className="col-6">
              <div className="d-flex align-items-center p-2 bg-light rounded-2 border">
                <div className="bg-warning bg-opacity-10 rounded-circle p-1 me-2">
                  <i className="ri-home-line text-warning"></i>
                </div>
                <div className="flex-grow-1">
                  <div className="fw-bold text-warning fs-5">
                    {data.filter(item => {
                      const hostname = item.referrerHostname || '';
                      return hostname === 'null';
                    }).length}
                  </div>
                  <small className="text-muted">Direto</small>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="mt-3">
            <TableContainer
              divClass="table-responsive"
              columns={columns}
              data={data}
              isGlobalFilter={false}
              customPageSize={5}
              initialSorting={[{ id: 'impression', desc: true }]}
            />
          </div>
        </CardBody>
      </Card>
    );
  }
);

ReferrerCard.displayName = 'ReferrerCard';

export default ReferrerCard;
